import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaCircle,
  FaComment,
  FaDollarSign,
  FaStar,
  FaUser,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaBriefcase,
  FaMoneyBillWave,
  FaAward
} from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './Bookings.css';

const BookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  // Form States
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');

  const fetchBooking = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${ENDPOINTS.BOOKINGS.base}/${id}`);
      // Handle both { data: {...} } and direct response
      const data = response.data || response;
      setBooking(data);
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await api.get(ENDPOINTS.AUDIT.LOGS, {
        params: {
          entityId: id,
          entityType: 'booking' // Changed from 'Booking' to 'booking' to match backend enum
        }
      });
      // Handle both array response or paginated response structure
      const logs = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setAuditLogs(logs);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  useEffect(() => {
    fetchBooking();
    fetchAuditLogs();
  }, [id]);

  const handleCancelBooking = async () => {
    if (!cancelReason.trim()) {
      toast.warning('Please provide a reason for cancellation');
      return;
    }
    try {
      await api.post(`${ENDPOINTS.BOOKINGS.base}/${id}/cancel`, { reason: cancelReason });
      toast.success('Booking cancelled successfully');
      setShowCancelModal(false);
      fetchBooking(); // Refresh data
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel booking');
    }
  };

  const handleIssueRefund = () => {
    if (window.confirm('Are you sure you want to issue a full refund?')) {
      toast.info('Refund feature coming soon ');
    }
  };

  const handleReschedule = () => {
    setShowRescheduleModal(true);
    // Initialize with current values
    if (booking) {
      // Assuming scheduledDate is ISO string "YYYY-MM-DD..."
      const datePart = booking.scheduledDate ? booking.scheduledDate.split('T')[0] : '';
      setRescheduleDate(datePart);
      setRescheduleTime(booking.scheduledTime || '');
    }
  };

  const submitReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) {
      toast.warning('Please select both date and time');
      return;
    }
    try {
      await api.post(`${ENDPOINTS.BOOKINGS.base}/${id}/reschedule`, {
        newDate: rescheduleDate,
        newTime: rescheduleTime
      });
      toast.success('Booking rescheduled successfully');
      setShowRescheduleModal(false);
      fetchBooking();
    } catch (error) {
      console.error('Reschedule error:', error);
      toast.error('Failed to reschedule booking');
    }
  };

  const handleHoldPayout = async () => {
    try {
      await api.post(`${ENDPOINTS.BOOKINGS.base}/${id}/hold-payout`);
      toast.success(booking.payoutHeld ? 'Payout released' : 'Payout held');
      fetchBooking();
    } catch (error) {
      console.error('Hold payout error:', error);
      toast.error('Failed to update payout status');
    }
  };

  const handleFlagBooking = async () => {
    try {
      await api.post(`${ENDPOINTS.BOOKINGS.base}/${id}/flag`);
      toast.success(booking.isFlagged ? 'Booking unflagged' : 'Booking flagged');
      fetchBooking();
    } catch (error) {
      console.error('Flag booking error:', error);
      toast.error('Failed to update flag status');
    }
  };

  const handleCompleteSession = async (sessionId) => {
    try {
      const response = await api.post(`${ENDPOINTS.BOOKINGS.base}/session/${sessionId}/complete`);
      toast.success(response.data?.message || 'Session completed');
      fetchBooking(); // Refresh for up-to-date progress
    } catch (error) {
      console.error('Complete session error:', error);
      toast.error(error.response?.data?.message || 'Failed to complete session');
    }
  };

  if (loading) return (
    <div className="loading-spinner-container">
      <div className="modern-spinner"></div>
      <p className="loading-text">Fetching Booking Data...</p>
    </div>
  );
  if (!booking) return (
    <div className="error-container">
      <div className="error-icon">⚠️</div>
      <p>Booking details not found.</p>
      <button onClick={() => navigate('/bookings')} className="btn btn-primary">Go Back</button>
    </div>
  );

  const formatDateToSlash = (dateStr) => {
    if (!dateStr) return 'N/A';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) return dateStr;
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatTimeRange = (timeStr, durationHours = 1) => {
    if (!timeStr) return 'N/A';
    const trimmed = timeStr.trim();
    const match = trimmed.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
    if (!match) return timeStr;

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const modifier = match[3];

    if (modifier) {
      const modUpper = modifier.toUpperCase();
      if (modUpper === 'PM' && hours < 12) hours += 12;
      if (modUpper === 'AM' && hours === 12) hours = 0;
    }

    const startMins = hours * 60 + minutes;
    const endMins = startMins + (durationHours * 60);

    const formatTimeMins = (mins) => {
      let h = Math.floor(mins / 60) % 24;
      const m = mins % 60;
      const suffix = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} ${suffix}`;
    };

    return `${formatTimeMins(startMins)} - ${formatTimeMins(endMins)}`;
  };

  const getConfinementEndDate = (startDateStr) => {
    if (booking?.endDate) return booking.endDate;
    if (!startDateStr) return '';
    const dateObj = new Date(startDateStr + 'T00:00:00');
    dateObj.setDate(dateObj.getDate() + 29); // 30 days total
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const schedulesToDisplay = booking?.schedules && booking.schedules.length > 0
    ? booking.schedules
    : booking?.scheduledDate
      ? [{ date: booking.scheduledDate, time: booking.scheduledTime }]
      : [];

  const completedSchedules = schedulesToDisplay.filter(s => {
    if (booking?.status === 'completed') return true;
    if (booking?.status === 'cancelled') return false;
    const todayStr = new Date().toISOString().split('T')[0];
    return s.date < todayStr;
  });

  return (
    <div className="booking-details-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/bookings')}>
            <FaArrowLeft /> Back
          </button>
          <div>
            <h1>Bookings</h1>
            <p className="page-subtitle">Manage and monitor all platform bookings</p>
          </div>
        </div>
      </div>

      {/* Booking Header */}
      <div className="booking-header-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <h2>Booking ID: #{booking.bookingNumber || booking.id}</h2>
          {booking.isFlagged && <span className="status-badge status-cancelled">FLAGGED</span>}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="booking-details-grid">
        {/* Left Column */}
        <div className="booking-left-column">
          {/* Customer & Provider Details */}
          <div className="content-card">
            <h3 className="card-title">Customer & Provider Details</h3>

            {/* Customer */}
            <div className="person-section">
              <h4>
                <FaUser className="section-icon" /> Customer Information
              </h4>
              <div className="person-card premium-card">
                <div className="avatar-wrapper">
                  <img
                    src={booking.customer?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(booking.customer?.firstName || '')}+${encodeURIComponent(booking.customer?.lastName || '')}&background=50C8AC&color=fff&bold=true&size=128`}
                    alt={booking.customer?.firstName || 'Customer'}
                    className="person-avatar"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(booking.customer?.firstName || 'U')}&background=50C8AC&color=fff&bold=true`;
                    }}
                  />

                  {booking.customer?.loyaltyTier && (
                    <span className={`tier-badge ${booking.customer.loyaltyTier}`}>
                      {booking.customer.loyaltyTier.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="person-info-enhanced">
                  <div className="info-header">
                    <h5>{booking.customer ? `${booking.customer.firstName || ''} ${booking.customer.lastName || ''}`.trim() : 'N/A'}</h5>
                    {booking.customer?.isFlagged && <span className="flag-mini">FLAGGED</span>}
                  </div>

                  <div className="info-grid-simple">
                    <div className="info-item-mini">
                      <span className="label">EMAIL</span>
                      <span className="value">{booking.customer?.email || 'N/A'}</span>
                    </div>
                    <div className="info-item-mini">
                      <span className="label">PHONE</span>
                      <span className="value">{booking.customer?.phone || 'N/A'}</span>
                    </div>
                    <div className="info-item-mini">
                      <span className="label">ADDRESS</span>
                      <span className="value">
                        <FaMapMarkerAlt className="mini-icon" />
                        {booking.customer?.address || booking.customer?.city ?
                          `${booking.customer.address || ''}${booking.customer.city ? ', ' + booking.customer.city : ''}` :
                          'No address provided'}
                      </span>
                    </div>
                  </div>

                  <div className="person-metrics">
                    <div className="metric">
                      <span className="metric-label">Total Bookings</span>
                      <span className="metric-value">{booking.customer?.totalBookings || 0}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Total Spent</span>
                      <span className="metric-value">${Number(booking.customer?.totalSpent || 0).toFixed(2)}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Member Since</span>
                      <span className="metric-value">
                        {booking.customer?.createdAt ? new Date(booking.customer.createdAt).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Provider */}
            <div className="person-section">
              <h4>
                <FaBriefcase className="section-icon" /> Provider Information
              </h4>
              <div className="person-card premium-card provider-theme">
                <div className="avatar-wrapper">
                  <img
                    src={booking.provider?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(booking.provider?.firstName || '')}+${encodeURIComponent(booking.provider?.lastName || '')}&background=4834d4&color=fff&bold=true&size=128`}
                    alt={booking.provider?.firstName || 'Provider'}
                    className="person-avatar"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(booking.provider?.firstName || 'P')}&background=4834d4&color=fff&bold=true`;
                    }}
                  />

                  <span className={`status-mini ${booking.provider?.status || 'pending'}`}>
                    {(booking.provider?.status || 'PENDING').toUpperCase()}
                  </span>
                </div>
                <div className="person-info-enhanced">
                  <div className="info-header">
                    <h5>{booking.provider ? `${booking.provider.firstName || ''} ${booking.provider.lastName || ''}`.trim() : 'N/A'}</h5>
                    <div className="rating-mini">
                      <FaStar className="star-icon" />
                      <span>{Number(booking.provider?.rating || 0).toFixed(1)}</span>
                    </div>
                  </div>

                  <div className="info-grid-simple">
                    <div className="info-item-mini">
                      <span className="label">EMAIL</span>
                      <span className="value">{booking.provider?.email || 'N/A'}</span>
                    </div>
                    <div className="info-item-mini">
                      <span className="label">PHONE</span>
                      <span className="value">{booking.provider?.phone || 'N/A'}</span>
                    </div>
                    <div className="info-item-mini">
                      <span className="label">ADDRESS</span>
                      <span className="value">
                        <FaMapMarkerAlt className="mini-icon" />
                        {booking.provider?.address || 'No address provided'}
                      </span>
                    </div>
                  </div>

                  <div className="person-metrics">
                    <div className="metric">
                      <span className="metric-label">Experience</span>
                      <span className="metric-value">{booking.provider?.yearsOfExperience || 0} Years</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Hourly Rate</span>
                      <span className="metric-value">${Number(booking.provider?.hourlyRate || 0).toFixed(2)}/hr</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Earnings</span>
                      <span className="metric-value">${Number(booking.provider?.totalEarnings || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Service Location Section */}
          <div className="content-card">
            <h3 className="card-title">
              <FaMapMarkerAlt className="section-icon" /> Service Location
            </h3>
            <div className="location-box">
              <div className="location-header">
                <span className="location-label">{booking.serviceLocation?.label?.toUpperCase() || 'BOOKING ADDRESS'}</span>
                {booking.serviceLocation?.unitNumber && <span className="unit-tag">Unit: {booking.serviceLocation.unitNumber}</span>}
              </div>
              <p className="full-address">{booking.serviceLocation?.address || 'No specific location provided'}</p>
              <div className="location-meta">
                <span>City: {booking.serviceLocation?.city || 'N/A'}</span>
                <span>Zip Code: {booking.serviceLocation?.zipCode || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Customer Notes */}
          <div className="content-card">
            <h3 className="card-title">Customer Notes</h3>
            <div className="notes-box">
              <p>{booking.customerNotes || 'No notes provided.'}</p>
            </div>
          </div>

          {/* Booking Timeline */}
          {/* <div className="content-card">
            <h3 className="card-title">Booking History</h3>
            <div className="timeline">
              {auditLogs.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px !important' }}>
                  <p style={{ color: '#636e72', fontSize: '14px', margin: 0 }}>No history available for this booking.</p>
                </div>
              ) : (
                auditLogs.map((log, index) => (
                  <div key={log.id || index} className="timeline-item">
                    <div className={`timeline-icon ${log.action === 'COMPLETED' ? 'completed' : 'pending'}`}>
                      <FaCircle style={{ fontSize: '10px' }} />
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-label">
                        {log.action}
                        {log.adminName && <span style={{ fontWeight: 'normal', color: '#636e72', fontSize: '12px' }}> by {log.adminName}</span>}
                      </div>
                      <div className="timeline-date">
                        {new Date(log.timestamp || log.createdAt).toLocaleString()}
                      </div>
                      {log.newValue && log.newValue.status && (
                        <div style={{ marginTop: '4px' }}>
                          <span className={`status-badge status-${log.newValue.status.toLowerCase()}`}>{log.newValue.status}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div> */}

          {/* Communication */}
          {/* <div className="content-card">
            <h3 className="card-title">Communication</h3>
            <div className="communication-buttons">
              <button className="btn btn-primary">
                <FaComment /> Open Chat with Customer
              </button>
              <button className="btn btn-primary">
                <FaComment /> Open Chat with Provider
              </button>
            </div>
          </div> */}

          {/* Payment Details */}
          <div className="content-card">
            <h3 className="card-title">Payment Details</h3>
            <div className="payment-breakdown">
              <div className="payment-row">
                <span>Service Amount:</span>
                <span>${Number(booking.serviceAmount || 0).toFixed(2)}</span>
              </div>
              {Number(booking.discountAmountOfLoyalty || 0) > 0 && (
                <div className="payment-row highlight-discount">
                  <span>Loyalty Discount:</span>
                  <span style={{ color: '#52c41a' }}>-${Number(booking.discountAmountOfLoyalty).toFixed(2)}</span>
                </div>
              )}
              <div className="payment-row subtotal-row">
                <span>Subtotal:</span>
                <span>${(Number(booking.serviceAmount || 0) - Number(booking.discountAmountOfLoyalty || 0)).toFixed(2)}</span>
              </div>
              <div className="payment-row">
                <span>Platform Commission:</span>
                <span>+$0.00</span>
              </div>
              <div className="payment-row">
                <span>Gateway Fee (10%):</span>
                <span>+${Number(booking.gatewayFee || 0).toFixed(2)}</span>
              </div>
              {Number(booking.taxes || 0) > 0 && (
                <div className="payment-row">
                  <span>Taxes:</span>
                  <span>${Number(booking.taxes || 0).toFixed(2)}</span>
                </div>
              )}
              <div className="payment-row total">
                <span>Total Amount:</span>
                <span>${booking.isDeposit ? Number(booking.depositAmount || 0).toFixed(2) : Number(booking.totalAmount || 0).toFixed(2)}</span>
              </div>

              {booking.isDeposit && (
                <div className="payment-deposit-breakdown" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px dashed #e2e8f0' }}>
                  <div className="payment-row">
                    <span style={{ color: '#10b981', fontWeight: '600' }}>Deposit Paid (via Platform):</span>
                    <span style={{ color: '#10b981', fontWeight: '600' }}>${Number(booking.depositAmount || 0).toFixed(2)}</span>
                  </div>
                  <div className="payment-row">
                    <span style={{ color: '#64748b' }}>Remaining Balance (Direct to Nanny):</span>
                    <span style={{ color: '#64748b' }}>${Number(booking.balanceAmount || 0).toFixed(2)}</span>
                  </div>
                </div>
              )}

              {(booking.isDeposit ||
                booking.service?.category?.name?.toLowerCase().includes('confinement') ||
                booking.service?.category?.slug?.toLowerCase().includes('confinement')) && (
                  <div className="payment-setup-notice" style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', padding: '12px', borderRadius: '8px', marginTop: '16px', fontSize: '0.85rem', color: '#0369a1', display: 'flex', gap: '10px' }}>
                    <FaMoneyBillWave style={{ marginTop: '3px', flexShrink: 0 }} />
                    <div>
                      <strong>Payment Setup:</strong> This is a Confinement Nanny service. A 50% deposit is collected via the platform. The remaining 50% balance must be paid directly to the nanny upon service completion.
                    </div>
                  </div>
                )}
              <div className="payment-details-footer">
                <div className="p-footer-item">
                  <span className="p-label">PAYMENT METHOD</span>
                  <span className="p-value">{(booking.payment?.paymentMethod || 'N/A').replace('_', ' ').toUpperCase()}</span>
                </div>
                <div className="p-footer-item">
                  <span className="p-label">TRANSACTION ID</span>
                  <span className="p-value">{booking.payment?.transactionId || 'N/A'}</span>
                </div>
                <div className="p-footer-item">
                  <span className="p-label">PAID AT</span>
                  <span className="p-value">
                    {booking.payment?.paidAt ? new Date(booking.payment.paidAt).toLocaleString() : 'N/A'}
                  </span>
                </div>
              </div>
              {booking.payoutHeld && (
                <div className="payment-row held-status" style={{ background: '#fff1f0', color: '#f5222d', fontWeight: 'bold' }}>
                  <span>Payout Status:</span>
                  <span>HELD</span>
                </div>
              )}
            </div>
          </div>

          {/* Internal Admin Notes */}
          <div className="content-card">
            <h3 className="card-title">Internal Admin Notes</h3>
            <textarea
              className="admin-notes-textarea"
              placeholder="Enter notes here..."
              rows="4"
              defaultValue={booking.internalNotes}
            ></textarea>
            <button className="btn btn-secondary">Save Notes</button>
          </div>

          {/* Audit Log Link */}
          {/* <div className="content-card">
            <button className="btn-link" onClick={() => navigate('/audit-log')}>
              View Audit Log
            </button>
          </div> */}
        </div>

        {/* Right Column - Actions */}
        <div className="booking-right-column">
          {/* Sessions & Progress Card */}
          <div className="content-card sessions-card">
            <div className="card-header-flex">
              <h3 className="card-title">Sessions & Progress</h3>
              {/* {schedulesToDisplay.length > 0 && (
                <span className="progress-text">
                  {completedSchedules.length} / {schedulesToDisplay.length} DONE
                </span>
              )} */}
            </div>

            <div className="sessions-list">
              {schedulesToDisplay.length > 0 ? (
                schedulesToDisplay.map((schedule, idx) => {
                  const isCompleted = booking.status === 'completed' || (booking.status !== 'cancelled' && schedule.date < new Date().toISOString().split('T')[0]);
                  const isConfinementNanny = booking.service?.category?.name?.toLowerCase().includes('confinement') ||
                                             booking.service?.category?.slug?.toLowerCase().includes('confinement');
                  return (
                    <div key={idx} className={`session-item ${isCompleted ? 'completed' : 'pending'}`}>
                      <div className="session-info">
                        <div className="session-main">
                          {/* <span className="session-number">#{idx + 1}</span> */}
                          {isConfinementNanny ? (
                            <>
                              <span className="session-date">
                                {formatDateToSlash(schedule.date)}
                              </span>
                              &nbsp;to&nbsp;
                              <span className="session-date">
                                {formatDateToSlash(schedule.endDate || getConfinementEndDate(schedule.date))}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="session-date">
                                {formatDateToSlash(schedule.date)}
                              </span>&nbsp;&nbsp;&nbsp;
                              <span className="session-time">
                                {formatTimeRange(schedule.time, schedule.durationHours || 1)}
                              </span>
                            </>
                          )}
                        </div>

                      </div>

                      {/* <div className="session-status-action">
                        {isCompleted ? (
                          <span className="session-completed-tag">
                            <FaCheckCircle /> COMPLETED
                          </span>
                        ) : booking.status === 'cancelled' ? (
                          <span className="text-muted" style={{ fontSize: '0.85rem' }}>CANCELLED</span>
                        ) : (
                          <span className="status-badge status-pending" style={{ fontSize: '0.85rem', margin: 0, padding: '2px 8px' }}>PENDING</span>
                        )}
                      </div> */}
                    </div>
                  );
                })
              ) : (
                <div className="session-item">
                  <div className="session-info">
                    <span className="text-muted">No schedule data available</span>
                  </div>
                </div>
              )}
            </div>

            {schedulesToDisplay.length > 0 && (
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{
                    width: `${(completedSchedules.length / schedulesToDisplay.length) * 100}%`
                  }}
                ></div>
              </div>
            )}
          </div>

          {/* Actions Card */}
          <div className="content-card actions-card">
            <h3 className="card-title">Actions</h3>
            <div className="status-display">
              <span>Current Status:</span>
              <span className={`status-badge status-${(booking.status || 'pending').toLowerCase()}`}>{booking.status || 'Pending'}</span>
            </div>

            <div className="action-buttons-vertical">
              {(booking.status !== 'cancelled' && booking.status !== 'completed') && (
                <button
                  className="btn btn-danger"
                  onClick={() => setShowCancelModal(true)}
                >
                  Cancel Booking
                </button>
              )}

              {/* <button
                className="btn btn-warning"
                onClick={handleIssueRefund}
              >
                Issue Refund
              </button> */}

              {/* {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                <button
                  className="btn btn-primary"
                  onClick={handleReschedule}
                >
                  Reschedule Booking
                </button>
              )} */}
            </div>
          </div>

          {/* Secondary Actions */}
          {/* <div className="content-card">
            <h3 className="card-title">Secondary Actions</h3>
            <div className="action-buttons-vertical">
              <button
                className={`btn ${booking.payoutHeld ? 'btn-success' : 'btn-secondary'}`}
                onClick={handleHoldPayout}
              >
                {booking.payoutHeld ? 'Release Payout' : 'Hold Payout'}
              </button>
              <button
                className={`btn ${booking.isFlagged ? 'btn-success' : 'btn-secondary'}`}
                onClick={handleFlagBooking}
              >
                {booking.isFlagged ? 'Unflag Booking' : 'Flag Booking'}
              </button>
            </div>
          </div> */}
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Booking</h3>
              <button
                className="modal-close"
                onClick={() => setShowCancelModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Please provide a reason for cancellation:</p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter cancellation reason..."
                rows="4"
                className="modal-textarea"
              />
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowCancelModal(false)}
              >
                Close
              </button>
              <button
                className="btn btn-danger"
                onClick={handleCancelBooking}
              >
                Confirm Cancellation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <div className="modal-overlay" onClick={() => setShowRescheduleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reschedule Booking</h3>
              <button
                className="modal-close"
                onClick={() => setShowRescheduleModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>New Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                />
              </div>
              <div className="form-group" style={{ marginTop: '15px' }}>
                <label>New Time</label>
                <input
                  type="time"
                  className="form-control"
                  value={rescheduleTime}
                  onChange={(e) => setRescheduleTime(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowRescheduleModal(false)}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={submitReschedule}
              >
                Confirm Reschedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingDetails;